import type { Source } from "@/types";

/**
 * Post-login landing route resolution.
 *
 * Faithfully ports the sidebar scope rules the old `login-with-token` and
 * `verify-otp` pages each duplicated inline: given the authenticated user's
 * normalized source (scope), pick the first sidebar entry they're allowed to
 * see, preferring the dashboard.
 */
export interface SidebarItem {
  id: string;
  href: string;
  scope: string[];
}

export const SIDEBAR_ITEMS: readonly SidebarItem[] = [
  { id: "dashboard", href: "/dashboard", scope: ["pglx", "clinic", "payer", "employer"] },
  { id: "clinics", href: "/clinics", scope: ["pglx", "payer"] },
  { id: "partners", href: "/partners", scope: ["pglx"] },
  { id: "employer", href: "/employers", scope: ["pglx", "employer"] },
  { id: "groups", href: "/groups", scope: ["pglx", "clinic", "payer", "employer"] },
  { id: "divisions", href: "/divisions", scope: ["pglx", "clinic", "payer", "employer"] },
  { id: "locations", href: "/locations", scope: ["pglx", "clinic", "payer", "employer"] },
  { id: "members", href: "/members", scope: ["pglx", "clinic", "payer", "employer"] },
  { id: "claims", href: "/claims", scope: ["pglx", "clinic", "payer", "employer"] },
  { id: "payments", href: "/payments", scope: ["pglx", "clinic", "payer", "employer"] },
  { id: "reconcillation", href: "/reconcillation", scope: ["pglx", "clinic"] },
  { id: "reports", href: "/reports", scope: ["pglx", "clinic", "payer", "employer"] },
  { id: "users", href: "/users", scope: ["pglx"] },
  { id: "settings", href: "/settings", scope: ["pglx", "clinic", "payer"] },
  {
    id: "billing-preferences",
    href: "/billing-preferences",
    scope: ["pglx", "clinic", "payer", "employer"],
  },
] as const;

export const SIDEBAR_RULES: Record<string, string[]> = {
  pglx: SIDEBAR_ITEMS.map((it) => it.id),
  clinic: [
    "dashboard",
    "clinics",
    "claims",
    "payments",
    "reconcillation",
    "reports",
    "users",
    "settings",
  ],
  payer: SIDEBAR_ITEMS.map((it) => it.id).filter((id) => id !== "clinics" && id !== "partners"),
  employer: [
    "dashboard",
    "employer",
    "groups",
    "divisions",
    "locations",
    "members",
    "hsa-members",
    "claims",
    "payments",
    "reports",
    "billing-preferences",
  ],
  group: [
    "dashboard",
    "groups",
    "divisions",
    "locations",
    "members",
    "hsa-members",
    "claims",
    "payments",
    "reports",
    "billing-preferences",
  ],
  division: [
    "dashboard",
    "divisions",
    "locations",
    "members",
    "hsa-members",
    "claims",
    "payments",
    "reports",
    "billing-preferences",
  ],
  location: [
    "dashboard",
    "locations",
    "members",
    "hsa-members",
    "claims",
    "payments",
    "reports",
    "billing-preferences",
  ],
  member: [
    "dashboard",
    "members",
    "hsa-members",
    "claims",
    "payments",
    "reports",
    "billing-preferences",
  ],
};

/** Resolve the landing route for a given scope (defaults to `/dashboard`). */
export function getRedirectHref(scope: Source | string | undefined): string {
  const enforcedIds = scope ? SIDEBAR_RULES[scope] : undefined;
  const items = SIDEBAR_ITEMS.filter((it) => {
    if (enforcedIds) return enforcedIds.includes(it.id);
    if (scope) return it.scope.includes(scope);
    return true;
  });
  const dashboardItem = items.find((it) => it.id === "dashboard");
  if (dashboardItem) return dashboardItem.href;
  return items[0]?.href ?? "/dashboard";
}
